import { Draggable, DraggableProvided } from "@hello-pangea/dnd";
import { ContentCopy, DragIndicator } from "@mui/icons-material";
import {
  Button,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ApiError,
  PlanTemplate,
  PlanWeek,
  PlanWorkout,
  WorkoutStatusEnum,
} from "@readyfit-common/models";
import { handleApiError } from "@readyfit-common/utils";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { EditIcon, TrashIcon } from "src/grading/assets";
import { LoadingButton } from "src/grading/components";
import { adjustDateOffset } from "src/grading/utils/dateHelpers";
import { useAppDispatch } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  setDeleteWeekModalOpen,
  setSelectedWeek,
  useCopyPlanTemplateWeekMutation,
  useGetPlanTemplatesLibraryQuery,
  useLazyGetPlanTemplateQuery,
  useUpdatePlanTemplateWeekMutation,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import Days from "../Days/Days";
import useStyles from "./ListWeeks.style";

interface ListWeeksProps {
  week: PlanWeek;
  numberOfWeeks: number;
  index: number;
  // getPlan: any;
  currentWeeks: number;
  workouts: PlanWorkout[];
  isEditable?: boolean;
  isTemplate?: boolean;
}

export const ListWeeks = ({
  week,
  index,
  currentWeeks,
  numberOfWeeks,
  workouts,
  isEditable,
  isTemplate,
}: ListWeeksProps) => {
  const css = useStyles();
  const [isInput, setIsInput] = useState(false);
  const [weekDescription, setWeekDescription] = useState(week.description);
  const [isEditableState, setIsEditableState] = useState(isEditable);
  const dispatch = useAppDispatch();
  const [updatePlanTemplateWeek] = useUpdatePlanTemplateWeekMutation();
  const params = useParams<{ id: string }>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [planTemplateLibraryId, setPlanTemplateLibraryId] = useState("");
  const [toWeekId, setToWeekId] = useState("");
  const [copyPlanTemplateWeek, { isLoading: isCopyLoading }] =
    useCopyPlanTemplateWeekMutation();
  const { data: planTemplatesLibrary } = useGetPlanTemplatesLibraryQuery({
    limit: 99999,
    offset: 0,
  });
  const [getPlanTemplate] = useLazyGetPlanTemplateQuery();
  const [planTemplate, setPlanTemplate] = useState<PlanTemplate | null>(null);

  const setInput = () => setIsInput(!isInput);

  const handleSubmitDescription = async (
    e: React.FocusEvent<HTMLTextAreaElement>,
  ) => {
    try {
      if (isTemplate) {
        await updatePlanTemplateWeek({
          planTemplateId: params.id,
          phaseId: week.phaseId,
          weekId: week.id,
          description: weekDescription,
        }).unwrap();
      }

      dispatch(
        showSnackbar({
          open: true,
          message: `Week description updated successfully`,
          type: "success",
        }),
      );
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
    setInput();
  };

  const setValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;

    setWeekDescription(val);
  };

  const handleCopyWeek = async () => {
    try {
      await copyPlanTemplateWeek({
        // current plan template
        planId: params.id,
        phaseId: week.phaseId,
        // to plan template
        planTemplateId: planTemplateLibraryId,
        weekId: toWeekId,
      }).unwrap();

      dispatch(
        showSnackbar({
          open: true,
          message: `Week copied successfully`,
          type: "success",
        }),
      );
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
    setAnchorEl(null);
  };

  const handleChangePlanTemplateLibrary = async (id: string) => {
    try {
      const response = await getPlanTemplate(id).unwrap();

      setPlanTemplate(response.data.plan);
    } catch (error: unknown) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const wrapperHeader = (provided?: DraggableProvided, dispatch?: any) => {
    const completedWorkouts = workouts?.filter(
      (workout) => workout.workoutStatusId === WorkoutStatusEnum.didIt,
    );
    return (
      <Grid container className={css.header}>
        <Grid item xs={12} className={css.wrapperTitle}>
          <Grid className={css.wrapperTitleWeek}>
            <span className={css.titleWeek}>
              {provided && week.numberOfWeek > currentWeeks && (
                <div {...provided.dragHandleProps}>
                  <DragIndicator />
                </div>
              )}

              {!isTemplate && (
                <>
                  <Typography variant="h5">
                    {adjustDateOffset(week.start).format("MMM, DD")}
                  </Typography>
                  <div className={css.line} />
                </>
              )}
            </span>

            <Typography variant="h5">
              Week {week.numberOfWeek} of {numberOfWeeks}
            </Typography>
            <div className={css.line} />
            <div className={css.wrapperLastIcon}>
              {!isTemplate && (
                <Typography variant="h5">
                  {`${completedWorkouts?.length} / ${week.workouts?.length} completed`}
                </Typography>
              )}
              {week.numberOfWeek > currentWeeks && (
                <>
                  <TrashIcon
                    onClick={() => {
                      dispatch(setSelectedWeek(week));
                      dispatch(setDeleteWeekModalOpen(true));
                    }}
                    className={css.iconEdit}
                  />
                  {!isTemplate && (
                    <EditIcon
                      onClick={() => setIsEditableState(true)}
                      className={css.iconEdit}
                    />
                  )}
                  {isTemplate && (
                    <>
                      <Tooltip title="Copy week from template">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                          }}
                        >
                          <ContentCopy className={css.iconEdit} />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        id="weekly-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={() => {
                          setToWeekId("");
                          setAnchorEl(null);
                          setPlanTemplateLibraryId("");
                        }}
                        className={css.menuContainer}
                        MenuListProps={{
                          "aria-labelledby": "weekly-button",
                        }}
                        slotProps={{
                          paper: {
                            sx: {
                              borderRadius: theme.spacing(1),
                              width: "500px",
                            },
                          },
                        }}
                      >
                        <Grid container>
                          <Grid item xs={12}>
                            <Typography
                              variant="h5"
                              textAlign={"center"}
                              fontSize={"16px"}
                              fontWeight={"bold"}
                              style={{
                                paddingTop: theme.spacing(1),
                                paddingBottom: theme.spacing(1),
                              }}
                            >
                              Select Week from Template
                            </Typography>
                          </Grid>

                          <Grid
                            container
                            spacing={2}
                            className={css.menuGridContainer}
                          >
                            <Grid item xs={6}>
                              <InputLabel
                                htmlFor="from-week"
                                className={css.inputLabel}
                              >
                                <Typography variant="subtitle1">
                                  Plan Template
                                </Typography>
                              </InputLabel>
                              <Select
                                id="from-week"
                                variant="outlined"
                                fullWidth
                                value={planTemplateLibraryId}
                                className={css.selectInput}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setToWeekId("");
                                  setPlanTemplateLibraryId(e.target.value);
                                  handleChangePlanTemplateLibrary(
                                    e.target.value,
                                  );
                                }}
                              >
                                {planTemplatesLibrary?.data?.plans?.map(
                                  (planTemplateLibrary) => {
                                    return (
                                      <MenuItem
                                        key={planTemplateLibrary.id}
                                        value={planTemplateLibrary.id}
                                      >
                                        {planTemplateLibrary.name}
                                      </MenuItem>
                                    );
                                  },
                                )}
                              </Select>
                            </Grid>
                            <Grid item xs={6}>
                              <InputLabel
                                htmlFor="to-week"
                                className={css.inputLabel}
                              >
                                <Typography variant="subtitle1">
                                  Week
                                </Typography>
                              </InputLabel>
                              <Select
                                id="to-week"
                                variant="outlined"
                                fullWidth
                                value={toWeekId}
                                className={css.selectInput}
                                onChange={(e) => {
                                  setToWeekId(e.target.value);
                                }}
                                disabled={!planTemplate}
                              >
                                {planTemplate?.phaseTemplates?.[0]?.weekTemplates?.map(
                                  (week) => {
                                    return (
                                      <MenuItem key={week.id} value={week.id}>
                                        Week {week.numberOfWeek}
                                      </MenuItem>
                                    );
                                  },
                                )}
                              </Select>
                            </Grid>
                          </Grid>

                          <Grid
                            container
                            columnGap={theme.spacing(20)}
                            alignItems={"center"}
                            justifyContent={"center"}
                          >
                            <Button
                              variant="outlined"
                              sx={{ color: "#000", borderWidth: 0 }}
                              className={css.button}
                              onClick={() => setAnchorEl(null)}
                            >
                              Cancel
                            </Button>
                            <LoadingButton
                              label={"Save"}
                              variant="contained"
                              color="primary"
                              onClick={handleCopyWeek}
                              loading={isCopyLoading}
                              className={css.button}
                            >
                              Copy
                            </LoadingButton>
                          </Grid>
                        </Grid>
                      </Menu>
                    </>
                  )}
                </>
              )}
            </div>
          </Grid>
          <Typography variant="caption" className={css.titleCoachNote}>
            coach note
          </Typography>
          {isInput ? (
            <TextField
              placeholder="Your Note..."
              value={weekDescription}
              className={css.input}
              onBlur={handleSubmitDescription}
              onChange={setValue}
              multiline
            />
          ) : (
            <div className={css.description} onClick={setInput}>
              {weekDescription.length ? weekDescription : "Your Note..."}
            </div>
          )}
        </Grid>
      </Grid>
    );
  };

  return week.numberOfWeek > currentWeeks ? (
    <Draggable
      key={week.numberOfWeek.toString()}
      draggableId={week.numberOfWeek.toString()}
      index={index}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={css.wrapperPhases}
        >
          {wrapperHeader(provided, dispatch)}

          <Days
            week={week}
            isEditable={isEditableState}
            isTemplate={isTemplate}
          />
        </div>
      )}
    </Draggable>
  ) : (
    <Grid container className={css.wrapperPhases}>
      {wrapperHeader()}

      <Days week={week} />
    </Grid>
  );
};
